// Types from the package root — erased at build time, so they cost nothing.
import type {
  Locale,
  PlaceBetResponse,
  RegulatoryProfile,
  Slip,
  StakeStatus,
  StakeSuggestion,
} from '@geni/contracts';
// The arithmetic from the pure subpath, and this is not tidiness. `assessStake`
// is a *value*, so importing it from the package root pulls Zod and every
// schema in the package into a browser bundle on a betting page — 393 kB to
// decide whether 20 is less than 100. Exactly the defect chunk 13 found in the
// `@geni/i18n` import three lines below this one, caught the same way: by
// looking at the number esbuild printed.
import { assessStake, potentialPayout } from '@geni/contracts/money';
import type { OddsFormat } from '@geni/contracts/odds-format';
// `@geni/i18n/text` rather than the package root, and the reason is weight
// rather than tidiness. The root re-exports the bundle and regulatory helpers,
// which import contracts for its *schemas* — so a browser bundle that reached
// for one string helper pulled Zod and every one of its locale files in behind
// it: 372 kB minified, on a betting page, to fill `{age}` in a sentence. This
// subpath is the pure text layer, and the widget shrank to 20 kB.
import { interpolate } from '@geni/i18n/text';
import type { WidgetState } from './state.js';
import type { Teaser } from './teaser.js';

/**
 * What should be on screen, as data.
 *
 * Everything a bettor reads is decided here and nowhere else, which is what
 * makes "the widget renders the player's locale and the operator's RG flags"
 * something a test can assert rather than something someone has to open a
 * browser to believe.
 *
 * Every string is looked up from the bundle. There is deliberately no English
 * literal in this file: a hardcoded label is one that is never translated, and
 * it would survive every test written in English.
 */

export interface LegView {
  eventLabel: string;
  marketLabel: string;
  selectionLabel: string;
  price: number;
}

export interface SlipView {
  id: string;
  legs: LegView[];
  /**
   * The fixture this slip is about, for the card's heading.
   *
   * The legs' own event when they share one, and null when they do not — an
   * accumulator across three matches has no single event, and picking the first
   * leg's would name one match while pricing three.
   */
  eventLabel: string | null;
  /** "Single" / "Accumulator" / "Bet builder", from our own enum. */
  combinationLabel: string;
  totalPriceLabel: string;
  totalPrice: number;
  confirmLabel: string;
  confirmEnabled: boolean;
  /** Set when this slip is being re-offered at a price that moved. */
  priceMoved: { notice: string; action: string; previousTotalPrice: number } | null;
}

/** A one-tap amount, or the route to a deposit. Labels already resolved. */
export interface StakeActionView {
  kind: 'STAKE' | 'TOP_UP';
  /** Null for TOP_UP: how much to deposit is not ours to have a view about. */
  amount: number | null;
  /** For STAKE the template is filled by the renderer, which formats money. */
  label: string;
}

/**
 * The confirmation card, shown inside the conversation.
 *
 * Only ever built when the stake is `READY` — a card is a thing you press, and
 * one that appears while an amount is still in question is one somebody presses
 * before the question was answered.
 */
export interface BetCardView {
  slipId: string;
  title: string;
  /** The feed's own word, when it says. Never translated. */
  sport: string | null;
  eventLabel: string;
  /** One line per leg: what was picked, at what price, and how to drop it. */
  legs: (LegView & {
    /**
     * Names this leg back to the server when the minus is pressed.
     *
     * Carried on the card rather than looked up later, because by the time a
     * press happens the only thing the browser knows about the row is where it
     * was on screen — and position is exactly what stops being true the moment
     * a leg is removed.
     */
    selectionId: string;
  })[];
  /**
   * Whether a leg may be taken off, which needs there to be more than one.
   *
   * A slip reduced to nothing is not a smaller bet, it is no bet, and it would
   * leave the amount and the confirm button attached to an empty ticket. The
   * route refuses it too — this is the half that keeps the control from being
   * offered in the first place, so nobody presses a button that cannot work.
   */
  removable: boolean;
  /** What the minus is called, for anyone who cannot see it. */
  removeLabel: string;
  combinationLabel: string;
  oddsLabel: string;
  totalPrice: number;
  stakeLabel: string;
  stake: number;
  payoutLabel: string;
  payout: number;
  currency: string;
  confirmLabel: string;
  confirmEnabled: boolean;
}

/**
 * Everything about the amount, as data.
 *
 * Recomputed here rather than taken from the response, using the same
 * `assessStake` the route uses. That is what lets a tapped suggestion update
 * the card without a round trip while making it impossible for the two sides to
 * disagree about whether something is affordable: there is one function, and
 * both call it.
 */
export interface StakeView {
  slipId: string;
  status: StakeStatus;
  label: string;
  placeholder: string;
  amount: number | null;
  currency: string | null;
  /** Null when the operator has no account integration. Then nothing is checked. */
  balance: number | null;
  balanceLabel: string;
  /** The sentence explaining why this amount will not do. Null when it will. */
  problem: string | null;
  actions: StakeActionView[];
  /** Present from the moment an amount is workable, card or no card. */
  payoutLabel: string;
  payout: number | null;
  inputEnabled: boolean;
}

/**
 * The prompts offered before anybody has typed.
 *
 * Two sources, and the order is the point. The operator's own win, because they
 * can name a match that is on the board the visitor is looking at right now and
 * we cannot — the widget knows the ids of what is on screen, never the team
 * names. The written fallback is one line per sport from the string tables, so
 * an operator who sets nothing but `sport` still gets something in the bettor's
 * own language.
 */
export interface StarterPromptsView {
  title: string;
  /** Sent verbatim as the bettor's message when tapped. */
  prompts: string[];
}

export interface ResponsibleGamblingView {
  /**
   * The line that is always on screen. Short by design, and shown whether or
   * not the rest is expanded — `requireRgMessage` says the message must be
   * *displayed*, and a footer that hid all of it behind a press would be
   * answering a regulator with "it is one tap away".
   */
  short: string;
  /** Opens the full wording below. */
  openLabel: string;
  /** The full message and age notice, present only while expanded. */
  full: { message: string; ageNotice: string; closeLabel: string } | null;
}

/**
 * The two-tap verdict on an answer.
 *
 * `reasons` is populated only after the disapproving tap, which is what makes
 * the control one tap for approval and two for anything else. `thanks` closes
 * both — a bettor who has just said the answer was wrong does not need a
 * second sentence about it.
 */
export interface FeedbackView {
  helpful: string;
  notHelpful: string;
  /** Set once a verdict has been given; the control collapses to this. */
  thanks: string | null;
  /** The three reasons, offered only after a disapproving tap. */
  reasons: { id: 'WRONG_MATCH' | 'WRONG_BET' | 'MISUNDERSTOOD'; label: string }[];
}

export interface FeedbackFormView {
  title: string;
  placeholder: string;
  send: string;
  cancel: string;
}

export interface WidgetViewModel {
  /** The locale the strings actually came from — what `lang` should be set to. */
  lang: Locale;
  /** True when the bettor asked for a language we do not have. */
  usingFallbackLanguage: boolean;
  /** How prices are written, as the operator's page writes them. */
  oddsFormat: OddsFormat;

  /**
   * What the closed pill says. The brand, always — never a string table entry.
   *
   * `widget.launcher.label` is a call to action ("Describe a bet"), and the
   * pill used to reveal it on hover. Reading it here made the button rename
   * itself the first time somebody opened and closed the panel.
   */
  launcherLabel: string;

  /**
   * A translated hint of what the pill does, for a tooltip.
   *
   * Kept off the accessible name on purpose: the visible text is the brand, and
   * an accessible name that does not contain the visible label breaks voice
   * control — "click Geni AI" has to work. This rides along as a description
   * instead, which is additive rather than a replacement.
   */
  launcherHint: string;
  /**
   * The collapsed launcher's stage, when there is a sport to draw.
   *
   * Passed through rather than derived: which action is playing is decided on a
   * timer in the element, because it is a question about elapsed time and this
   * file is a pure function of state.
   */
  teaser: Teaser | null;
  title: string;
  subtitle: string;
  closeLabel: string;
  placeholder: string;
  sendLabel: string;

  open: boolean;
  busy: boolean;
  /** Shown while a parse is in flight. */
  thinkingLabel: string | null;

  messages: { from: 'player' | 'assistant'; text: string; at?: string }[];
  slips: SlipView[];
  slipTitle: string | null;

  /**
   * More to add to the slip on screen, ready to be pressed.
   *
   * Null wherever there is nothing to build on, which the server has already
   * decided — see `mostPopular` and the three refusals around it. The widget
   * draws what it is given and rules on nothing.
   */
  buildOn: {
    /** Always shown, so the row says what it is before anything is opened. */
    title: string;
    /** What the disclosure calls itself. Opening it is the only thing it does. */
    openLabel: string;
    open: boolean;
    /** Shown on the confirm, with the count of what is ticked. */
    confirmLabel: string;
    /** The bettor may also type, for anything the options do not cover. */
    placeholder: string;
    /** Naming a fixture that is not on the ticket yet. A different question. */
    addPlaceholder: string;
    /** Whether that second field is open. Closed, its place is the button. */
    typing: boolean;
    /** The word on the button beside the field. See renderBuildOn. */
    addLabel: string;
    options: { ref: string; label: string; price: number; picked: boolean }[];
    pickedCount: number;
  } | null;

  /**
   * The amount, when there is one bet and this operator collects it here.
   *
   * Null restores the behaviour that predates in-chat betting exactly: the slip
   * cards keep their own confirm buttons and a confirmed bet is handed to the
   * operator's own slip with a signed ticket. That is what a jurisdiction with
   * `allowStakeEntry: false` gets, and what a price search gets until the
   * bettor says which of the results they mean.
   */
  stake: StakeView | null;

  /** The one-click card. Non-null only once the amount is settled. */
  betCard: BetCardView | null;

  /** What the operator said when they were handed the bet. */
  placementNotice: string | null;

  /**
   * Suggestions for a bettor who has not said anything yet.
   *
   * Null the moment the conversation starts. A panel that keeps offering
   * openers underneath a conversation in progress is one that reads as though
   * it did not notice the bettor had begun.
   */
  starters: StarterPromptsView | null;

  /**
   * What the assistant is, said before anybody has to ask.
   *
   * A string from the bundle, not a message from the model, and that is the
   * whole point: "what can you do" was the commonest opening line and every one
   * of them bought a full parse — around a cent of tokens to say something that
   * never changes. Written once per language and served with the panel, it
   * costs nothing and arrives before the question instead of after it.
   *
   * Gated exactly like the starters below it: gone the moment a conversation
   * begins, and absent where the assistant may not operate, because an
   * introduction to something a bettor is about to be told they cannot use is
   * worse than silence.
   */
  greeting: string | null;

  /**
   * The sentence that explains an answer with no confirmable bet in it, or the
   * one that follows a bet handed off.
   *
   * These strings have shipped in five languages since chunk 10 and nothing
   * rendered them, so "understood, but this operator has nothing matching"
   * arrived as an empty panel. Derived from `presentation`, which says what
   * actually happened, rather than inferred from `slips.length === 0` — which
   * is also true of a question about the weather.
   */
  notice: string | null;

  /**
   * Responsible-gambling text, when the operator's jurisdiction requires it.
   *
   * Null is not "no rules apply" — it is "this jurisdiction does not require a
   * message here". The operator's own page carries its licensing footer either
   * way; this is about what the assistant shows beside a bet.
   */
  responsibleGambling: ResponsibleGamblingView | null;

  /**
   * The verdict control, when there is an answer worth having one about.
   *
   * Null on everything the bettor cannot sensibly rate: an empty panel, a
   * question back to them, a jurisdiction where the assistant is closed. Under
   * "how much do you want on it?" there is nothing to approve of yet, and a
   * control offered there would collect noise and train people to ignore it.
   */
  feedback: FeedbackView | null;

  /** The free-text form, when it is open. Reached from the panel, not an answer. */
  feedbackForm: FeedbackFormView | null;

  /**
   * The standing link that opens that form.
   *
   * Anchored in the footer rather than under the greeting, and the difference
   * is not cosmetic: the greeting is null the moment `messages.length > 0`, so
   * a link living there would be visible only before the bettor has anything
   * to complain about. This one survives the conversation starting, which is
   * when it becomes worth having.
   */
  feedbackOpenLabel: string | null;

  /**
   * Set when the assistant may not operate in this jurisdiction at all.
   *
   * When present the input is closed. The bettor gets a sentence rather than a
   * box that accepts text and always answers the same way.
   */
  unavailable: string | null;

  inputEnabled: boolean;
  error: string | null;
}

/** Nothing to show but a launcher: the panel is closed or has no strings yet. */
function stringsMissing(state: WidgetState): boolean {
  return state.bundle === null;
}

export function toViewModel(state: WidgetState): WidgetViewModel | null {
  const bundle = state.bundle;
  // Before the bundle arrives there is no language to render in, so there is
  // nothing honest to draw. The element shows its launcher and waits.
  if (stringsMissing(state) || bundle === null) return null;

  const s = (key: string): string => bundle.strings[key] ?? key;
  const regulatory: RegulatoryProfile = bundle.regulatory;

  const permitted = regulatory.assistantPermitted;
  const busy =
    state.phase === 'THINKING' ||
    state.phase === 'CONFIRMING' ||
    state.phase === 'PLACING';

  /** The one fixture a slip is about, or null when it spans several. */
  const sharedEvent = (legs: readonly { eventId: string; eventLabel: string }[]): string | null => {
    const first = legs[0];
    if (first === undefined) return null;
    return legs.every((leg) => leg.eventId === first.eventId) ? first.eventLabel : null;
  };

  /**
   * The stake, worked out from what the bettor has settled on.
   *
   * `state.stake` is the server's account of the last turn and carries the
   * account; `state.stakeAmount` is what they have done since. The assessment
   * runs over the second using the first, which is why tapping a suggested
   * amount changes the card without asking anybody.
   */
  const stakeContext = state.stake ?? null;
  const slip = state.slips.length === 1 ? state.slips[0] : undefined;

  const stakeView = ((): StakeView | null => {
    if (stakeContext === null || slip === undefined) return null;

    /* The operator's page first, ours second.
       `state.account` is what this browser was told by the operator, which is
       the only party that knows whose account it is. `stakeContext.account` is
       whatever our server managed to supply, which for a real operator is
       nothing — it does not know the bettor. Preferring the first means the
       server-side hook can stay for the demo without either of them fighting. */
    const account = state.account ?? stakeContext.account ?? null;
    const amount = state.stakeAmount;
    const assessment = assessStake({
      amount,
      account,
      allowTopUp: regulatory.allowTopUpPrompt,
    });

    return {
      slipId: slip.id,
      status: assessment.status,
      label: s('widget.stake.label'),
      placeholder: s('widget.stake.placeholder'),
      amount,
      currency: account?.currency ?? null,
      balance: account?.balance ?? null,
      balanceLabel: s('widget.stake.available'),
      problem: stakeProblem(assessment.status, s),
      actions: assessment.suggestions.map((suggestion) => stakeAction(suggestion, s)),
      payoutLabel: s('widget.stake.payout'),
      payout:
        assessment.status === 'READY' && amount !== null
          ? potentialPayout(amount, slip.totalPrice)
          : null,
      inputEnabled: !busy && permitted,
    };
  })();

  /**
   * The card, and the one place the whole feature comes together.
   *
   * Built only from a `READY` assessment and a re-priceable slip, so there is
   * no path to a Confirm button sitting above an amount nobody has agreed on.
   * A price that moved keeps the card on screen and disabled — see
   * `confirmEnabled` — because the bettor has to see the new number before
   * agreeing to it again.
   */
  const betCard = ((): BetCardView | null => {
    if (stakeView === null || slip === undefined) return null;
    if (stakeView.status !== 'READY' || stakeView.amount === null) return null;

    return {
      slipId: slip.id,
      title: s('widget.bet.summary'),
      sport: sportOf(slip),
      eventLabel: sharedEvent(slip.legs) ?? '',
      legs: slip.legs.map((leg) => ({
        eventLabel: leg.eventLabel,
        marketLabel: leg.marketLabel,
        selectionLabel: leg.selectionLabel,
        price: leg.price,
        selectionId: leg.selectionId,
      })),
      removable: slip.legs.length > 1 && !busy,
      removeLabel: s('widget.bet.removeLeg'),
      combinationLabel: s(`widget.slip.combination.${slip.combination}`),
      oddsLabel: s('widget.slip.totalPrice'),
      totalPrice: slip.totalPrice,
      stakeLabel: s('widget.stake.label'),
      stake: stakeView.amount,
      payoutLabel: s('widget.stake.payout'),
      payout: potentialPayout(stakeView.amount, slip.totalPrice),
      currency: stakeView.currency ?? '',
      confirmLabel:
        state.phase === 'PLACING' ? s('widget.bet.placing') : s('widget.bet.confirm'),
      // A moved price disables it rather than hiding it, so the bettor sees
      // what changed instead of watching the button disappear.
      confirmEnabled: !busy && permitted && state.movedFrom === null,
    };
  })();

  /**
   * What to say when the answer contains no bet to confirm.
   *
   * `REPLY_ONLY` deliberately produces nothing: the reply is the whole answer,
   * and adding "nothing matched" underneath a reply about the weather would be
   * answering a question nobody asked.
   */
  /**
   * Offered only where there is something to have an opinion about.
   *
   * A slip, a set of price matches, or a plain answer the assistant gave —
   * those are answers. A question back to the bettor is not one, and neither
   * is an empty panel or a closed jurisdiction. Asking "was that good?" under
   * "how much do you want on it?" collects noise and teaches people that the
   * control means nothing.
   */
  const feedbackFor = (): FeedbackView | null => {
    if (state.bundle === null || !state.bundle.regulatory.assistantPermitted) return null;
    if (state.answerId === null || state.phase === 'THINKING') return null;
    // Nothing was answered yet — the panel is still on its greeting.
    if (state.messages.length === 0) return null;

    return {
      helpful: s('widget.feedback.helpful'),
      notHelpful: s('widget.feedback.notHelpful'),
      thanks:
        state.verdict === 'HELPFUL' || state.verdict === 'NOT_HELPFUL'
          ? s('widget.feedback.thanks')
          : null,
      reasons:
        state.verdict === 'ASKED_WHY'
          ? [
              { id: 'WRONG_MATCH', label: s('widget.feedback.reason.wrongMatch') },
              { id: 'WRONG_BET', label: s('widget.feedback.reason.wrongBet') },
              { id: 'MISUNDERSTOOD', label: s('widget.feedback.reason.misunderstood') },
            ]
          : [],
    };
  };

  const noticeFor = (): string | null => {
    if (state.phase === 'HANDED_OFF') return s('widget.slip.confirmed');
    /**
     * Said here because the reply cannot say it.
     *
     * The model writes its sentence before the gate runs, so a request naming a
     * winner and a scoreline is described as two bets above a slip holding one.
     * Ahead of the switch below: those are all cases with no slip at all, so
     * the two can never both apply.
     */
    if (state.mergedLegs && state.slips.length > 0) return s('widget.slip.mergedLegs');
    switch (state.presentation) {
      case 'SEPARATE_SINGLES':
        return s('widget.slip.separateSingles');
      case 'NOTHING_MATCHED':
        return s('widget.slip.nothingMatched');
      case 'UNAVAILABLE':
        return s('widget.slip.unavailable');
      // A price search that found nothing. The empty string is its own sentence
      // because "nothing is priced near that" is a different answer from "I
      // could not find that bet".
      case 'PRICE_MATCHES':
        return state.slips.length === 0 ? s('widget.targetOdds.empty') : null;
      default:
        return null;
    }
  };

  return {
    lang: bundle.resolved,
    usingFallbackLanguage: bundle.usedFallback,
    oddsFormat: state.context.oddsFormat,

    launcherLabel: state.brand,
    launcherHint: s('widget.launcher.label'),
    teaser: state.teaser,
    title: s('widget.title'),
    subtitle: s('widget.subtitle'),
    closeLabel: s('widget.close'),
    placeholder: s('widget.input.placeholder'),
    sendLabel: s('widget.input.send'),

    open: state.phase !== 'CLOSED',
    busy,
    thinkingLabel: state.phase === 'THINKING' ? s('widget.thinking') : null,

    messages: state.messages.map((message) => ({ ...message })),
    /**
     * Present whenever there is one bet to grow, whatever the board quotes.
     *
     * It used to require quick options as well, and that tied three unrelated
     * things together. A fixture the supplier prices thinly — one where the
     * only market is the match result — has nothing to offer as chips, and the
     * whole row vanished: the free-text field with it, and "add another match"
     * with that. Adding a *different* fixture has nothing to do with how deep
     * this one is quoted.
     *
     * Seen on the live demo: Real Madrid vs Real Betis came back with one
     * market, so a bettor holding that bet had no way to add anything at all
     * except by starting again in the composer, which replaces the slip.
     *
     * The chips are what depend on there being alternatives, and only they.
     */
    buildOn:
      state.slips.length === 1
        ? {
            title: s('widget.buildOn.title'),
            openLabel: s('widget.buildOn.open'),
            open: state.buildOpen,
            confirmLabel: s('widget.buildOn.confirm'),
            placeholder: s('widget.buildOn.placeholder'),
            addPlaceholder: s('widget.buildOn.addPlaceholder'),
            typing: state.buildTyping,
            addLabel: s('widget.buildOn.add'),
            /**
             * Which match a chip belongs to, once there is more than one.
             *
             * An accumulator's chips are drawn from every fixture on the slip,
             * so two of them can be the same market: "Both Teams to Score:
             * Yes" at 1.45 above "Both Teams to Score: Yes" at 1.52, one from
             * each leg, and nothing on either saying which. Seen on the demo
             * within a minute of the chips being shared across fixtures.
             *
             * The same failure the choice chips already fixed — "Liverpool Fc
             * 1,52" directly above "Liverpool Fc 1,79" — arriving by a
             * different road.
             *
             * Named by the side the bettor backed there rather than the
             * fixture, because that is what they chose and it is half as long.
             * Only when the slip holds more than one: a single-fixture slip
             * has nothing to disambiguate and the suffix would be noise.
             */
            options: (state.alternatives[state.slips[0]!.id] ?? []).map((alternative) => {
              const legs = state.slips[0]!.legs;
              const backed =
                legs.length > 1
                  ? legs.find((leg) => leg.eventId === alternative.eventId)?.selectionLabel
                  : undefined;

              /**
               * And nothing to disambiguate once the chip has said it itself.
               *
               * A handicap names the side in its own label, so the suffix
               * repeated it: "Asian Handicap -1.25: Philadelphia Union -1.25 ·
               * Philadelphia Union". Found by opening the panel on a phone and
               * reading the chips, which is the only place this was ever going
               * to show.
               *
               * Dropping it is safe because the label is then doing the
               * suffix's whole job. A chip naming the *opponent* — "Sporting
               * Kansas City -1.25" on the leg where Seattle was backed — does
               * not match, so it keeps the suffix and stays distinguishable.
               */
              const side =
                backed !== undefined && alternative.label.toLowerCase().includes(backed.toLowerCase())
                  ? undefined
                  : backed;

              return {
                ref: alternative.ref,
                label:
                  side === undefined ? alternative.label : `${alternative.label} · ${side}`,
                price: alternative.price,
                picked: state.buildPicked.includes(alternative.ref),
              };
            }),
            pickedCount: state.buildPicked.length,
          }
        : null,
    slips: state.slips.map((slip) => ({
      id: slip.id,
      legs: slip.legs.map((leg) => ({
        eventLabel: leg.eventLabel,
        marketLabel: leg.marketLabel,
        selectionLabel: leg.selectionLabel,
        price: leg.price,
      })),
      eventLabel: sharedEvent(slip.legs),
      combinationLabel: s(`widget.slip.combination.${slip.combination}`),
      totalPriceLabel: s('widget.slip.totalPrice'),
      totalPrice: slip.totalPrice,
      confirmLabel: state.phase === 'CONFIRMING' ? s('widget.slip.confirming') : s('widget.slip.confirm'),
      confirmEnabled: !busy && permitted,
      priceMoved:
        state.movedFrom === null
          ? null
          : {
              notice: s('widget.slip.priceMoved'),
              action: s('widget.slip.priceMovedAction'),
              previousTotalPrice: state.movedFrom,
            },
    })),
    // A price search is not a bet the assistant picked, and heading it "Your
    // selections" would present a neutral filter as a recommendation — the one
    // thing the target-odds feature is required not to do.
    slipTitle:
      state.slips.length === 0
        ? null
        : state.presentation === 'CHOICES'
          ? s('widget.choices.title')
          : state.presentation === 'PRICE_MATCHES'
            ? s('widget.targetOdds.title')
            : s('widget.slip.title'),

    starters: startersFor(state, s),
    greeting:
      state.messages.length > 0 || !state.bundle?.regulatory.assistantPermitted
        ? null
        : s('widget.greeting'),

    stake: stakeView,
    betCard,
    placementNotice: placementNoticeFor(state.placement, state.phase, s),

    feedback: feedbackFor(),
    feedbackForm: state.feedbackFormOpen
      ? {
          title: s('widget.feedback.title'),
          placeholder: s('widget.feedback.placeholder'),
          send: s('widget.feedback.send'),
          cancel: s('widget.feedback.cancel'),
        }
      : null,
    feedbackOpenLabel:
      state.bundle === null || !state.bundle.regulatory.assistantPermitted
        ? null
        : s('widget.feedback.open'),

    notice: noticeFor(),

    responsibleGambling: regulatory.requireRgMessage
      ? {
          // Always on screen, whatever else is folded away.
          short: s('rg.short'),
          openLabel: s('rg.open'),
          full: state.rgOpen
            ? {
                // A jurisdiction strict enough to suppress a bet over a phrase
                // gets the longer message, which names the risk rather than
                // only the budget.
                message:
                  regulatory.safetyStrictness === 'STRICT'
                    ? s('rg.messageStrict')
                    : s('rg.message'),
                // The age is a number from the profile, not from the sentence,
                // so a jurisdiction at 21 does not need its own translation.
                ageNotice: interpolate(s('rg.ageLimit'), {
                  age: String(regulatory.minimumAge),
                }),
                closeLabel: s('rg.close'),
              }
            : null,
        }
      : null,

    unavailable: permitted ? null : s('rg.assistantUnavailable'),
    inputEnabled: permitted && !busy && state.phase !== 'FAILED',

    error:
      state.error === null
        ? null
        : s(
            state.error === 'RATE_LIMITED'
              ? 'widget.error.rateLimited'
              : state.error === 'SESSION'
                ? 'widget.error.session'
                : state.error === 'BOARD'
                  ? 'widget.error.board'
                  : state.error === 'SPENT'
                    ? 'widget.error.spent'
                    : 'widget.error.generic',
          ),
  };
}

/** Lookup with the same fallback-to-key behaviour the main builder uses. */
type Strings = (key: string) => string;

/**
 * What to offer somebody who has not typed yet.
 *
 * Shown only on an empty conversation, and only where the assistant can
 * actually answer — a prohibited jurisdiction closes the input, and chips
 * inviting a question that will be refused would be worse than no chips.
 */
function startersFor(state: WidgetState, s: Strings): StarterPromptsView | null {
  if (state.messages.length > 0) return null;
  if (!state.bundle?.regulatory.assistantPermitted) return null;

  const prompts = state.context.prompts.filter((prompt) => prompt.trim() !== '');
  if (prompts.length > 0) {
    return { title: s('widget.prompts.title'), prompts: [...prompts] };
  }

  const sport = state.context.sport;
  // `OTHER` has no written prompt on purpose: an operator trading something we
  // have nothing for gets no chips rather than a wrong one. Null means nobody
  // said what sport this is, which is the pre-context behaviour exactly.
  if (sport === null || sport === 'OTHER') return null;

  // Two written defaults per sport, so an operator who supplies nothing gets a
  // list rather than a lone chip. `s` falls back to the key itself for a
  // missing string, and a chip reading "widget.prompts.TENNIS" is worse than no
  // chip at all — so anything that came back as its own key is dropped.
  const written = [`widget.prompts.${sport}`, `widget.prompts.${sport}.alt`]
    .map((key) => ({ key, text: s(key) }))
    .filter((entry) => entry.text !== entry.key)
    .map((entry) => entry.text);

  return written.length === 0
    ? null
    : { title: s('widget.prompts.title'), prompts: written };
}

/**
 * Why an amount will not do, in words.
 *
 * `NEEDED` deliberately has no sentence. Not naming an amount yet is not a
 * problem the bettor has, and putting an error under the field the moment a
 * card appears would read as a telling-off for having done nothing wrong — the
 * assistant's own reply is what asks the question.
 */
function stakeProblem(status: StakeStatus, s: Strings): string | null {
  switch (status) {
    case 'EXCEEDS_BALANCE':
      return s('widget.stake.exceedsBalance');
    case 'BELOW_MINIMUM':
      return s('widget.stake.belowMinimum');
    case 'ABOVE_MAXIMUM':
      return s('widget.stake.aboveMaximum');
    default:
      return null;
  }
}

/**
 * A quick action, with its label resolved but its number left alone.
 *
 * The template keeps `{amount}` in it: formatting money needs the currency and
 * the language, and both belong to the renderer, which is where every other
 * number in this widget is formatted. Interpolating here would mean a second
 * money formatter, and two formatters is how a balance and a stake end up
 * written differently on the same card.
 */
function stakeAction(suggestion: StakeSuggestion, s: Strings): StakeActionView {
  return suggestion.kind === 'TOP_UP'
    ? { kind: 'TOP_UP', amount: null, label: s('widget.stake.topUp') }
    : { kind: 'STAKE', amount: suggestion.amount, label: s('widget.stake.quickAction') };
}

/**
 * The sport, when every leg agrees about it.
 *
 * An accumulator spanning tennis and football has no single sport, and naming
 * one of them on a card that prices both would be worse than naming none —
 * the same argument as `sharedEvent` above.
 */
function sportOf(slip: Slip): string | null {
  const first = slip.legs[0]?.sport;
  if (first === undefined || first === '') return null;
  return slip.legs.every((leg) => leg.sport === first) ? first : null;
}

/**
 * What the operator said, translated into one of our sentences.
 *
 * Their reason codes are mapped to our strings rather than shown as they
 * arrive: an operator's own error text is written for their support team, in
 * whatever language their backend was configured in, and it would land in the
 * middle of a conversation the bettor is having in theirs.
 */
function placementNoticeFor(
  placement: PlaceBetResponse | null,
  phase: WidgetState['phase'],
  s: Strings,
): string | null {
  if (placement === null) return null;
  if (placement.status === 'PLACED') {
    return phase === 'PLACED' ? s('widget.bet.placed') : null;
  }

  switch (placement.reason) {
    case 'INSUFFICIENT_FUNDS':
      return s('widget.bet.refused.insufficientFunds');
    case 'STAKE_OUT_OF_BOUNDS':
      return s('widget.bet.refused.stakeOutOfBounds');
    case 'TICKET_REJECTED':
      return s('widget.bet.refused.ticketRejected');
    case 'MARKET_CLOSED':
      return s('widget.bet.refused.marketClosed');
    case 'ACCOUNT_RESTRICTED':
      return s('widget.bet.refused.accountRestricted');
    default:
      return s('widget.bet.refused.generic');
  }
}
